import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import v4Api from 'lib/api/ssapi/secondStepApi.v4'
import {
  addInstructorEmailSchema,
  EMAIL_INACTIVE_MESSAGE,
  EMAIL_NOT_FOUND_MESSAGE,
} from 'lib/schemas/addInstructorSchema'
import AddInstructor from './component'

const getProfile = userProfile => userProfile?.profile || userProfile || {}

const buildOwnerFromProfile = userProfile => {
  const profile = getProfile(userProfile)
  const email = profile.email || profile.mail || ''
  if (!email) return null
  return {
    email,
    firstName: profile.firstName || profile.givenName || '',
    lastName: profile.lastName || profile.surname || '',
    isCurrentUser: true,
    role: 'owner',
    userId: profile.id,
  }
}

const mapSiteUserToPerson = (user, { isCurrentUser = false, role } = {}) => ({
  email: user.email || user.inviteEmail || '',
  firstName: user.firstName || '',
  lastName: user.lastName || '',
  isCurrentUser,
  role,
  userId: user.userId || user.id,
})

const AddInstructorContainer = ({
  addSecondaryTeacher,
  currentSite,
  dataTestId,
  fetchInstructorData,
  handleCancel,
  instructors,
  registrationId,
  secondaryUserIds,
  userContext,
  userProfile,
}) => {
  const initialPeople = useMemo(() => {
    if (Array.isArray(instructors) && instructors.length > 0) {
      return instructors
    }
    const owner = buildOwnerFromProfile(userProfile)
    return owner ? [owner] : []
  }, [instructors, userProfile])

  const [email, setEmail] = useState('')
  const [peopleWithAccess, setPeopleWithAccess] = useState(initialPeople)
  const [inputErrors, setInputErrors] = useState({ email: { errors: null } })
  const [lookupError, setLookupError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const secondaryUserIdsKey = Array.isArray(secondaryUserIds)
    ? secondaryUserIds.filter(id => id != null && id !== '').join(',')
    : ''

  useEffect(() => {
    if (Array.isArray(instructors) && instructors.length > 0) return undefined

    const siteId = currentSite?.siteId
    const ids = Array.isArray(secondaryUserIds)
      ? secondaryUserIds.filter(id => id != null && id !== '')
      : []
    if (!siteId || ids.length === 0) return undefined

    let cancelled = false
    const loadSecondaryTeachers = async () => {
      try {
        const response = await v4Api.getSiteUsers(siteId, { userIds: ids })
        if (cancelled) return
        const users = response?.data || []
        if (!Array.isArray(users) || users.length === 0) return

        const coTeachers = users
          .map(user => mapSiteUserToPerson(user, { role: 'co-teacher' }))
          .filter(person => person.userId || person.email)

        setPeopleWithAccess(prev => {
          const owner =
            prev.find(person => person.role === 'owner') ||
            buildOwnerFromProfile(userProfile)
          return owner ? [owner, ...coTeachers] : coTeachers
        })
      } catch (error) {
        // Keep owner-only list if lookup fails; user can still add instructors.
      }
    }

    loadSecondaryTeachers()
    return () => {
      cancelled = true
    }
  }, [currentSite?.siteId, instructors, secondaryUserIdsKey, userProfile])

  const handleEmailChange = useCallback(e => {
    setEmail(e?.target?.value ?? '')
    setLookupError(null)
    setInputErrors({ email: { errors: null } })
  }, [])

  const handleSubmit = useCallback(async () => {
    setLookupError(null)
    try {
      await addInstructorEmailSchema.validate({ email }, { abortEarly: false })
      setInputErrors({ email: { errors: null } })
    } catch (validationError) {
      const message =
        validationError.errors?.[0] ||
        validationError.message ||
        'Invalid email'
      setInputErrors({ email: { errors: message } })
      return
    }

    const siteId = currentSite?.siteId
    if (!siteId) {
      setLookupError(EMAIL_NOT_FOUND_MESSAGE)
      return
    }

    setIsSubmitting(true)
    try {
      const response = await v4Api.getSiteUsers(siteId, {
        inviteEmails: [email.trim()],
      })
      const users = response?.data || []
      if (!Array.isArray(users) || users.length === 0) {
        setLookupError(EMAIL_NOT_FOUND_MESSAGE)
        return
      }

      const matchedUser =
        users.find(
          user =>
            (user.email || user.inviteEmail || '').toLowerCase() ===
            email.trim().toLowerCase(),
        ) || users[0]

      if (matchedUser.isActive === false) {
        setLookupError(EMAIL_INACTIVE_MESSAGE)
        return
      }

      const userId = matchedUser.userId || matchedUser.id
      if (!userId) {
        setLookupError(EMAIL_NOT_FOUND_MESSAGE)
        return
      }

      const alreadyAdded = peopleWithAccess.some(
        person =>
          person.userId === userId ||
          (person.email || '').toLowerCase() ===
            (matchedUser.email || email).toLowerCase(),
      )
      if (alreadyAdded) {
        setEmail('')
        return
      }

      if (addSecondaryTeacher && registrationId) {
        await addSecondaryTeacher({ registrationId, userId })
      }

      setPeopleWithAccess(prev => [
        ...prev,
        {
          email: matchedUser.email || email.trim(),
          firstName: matchedUser.firstName || '',
          lastName: matchedUser.lastName || '',
          isCurrentUser: false,
          role: 'co-teacher',
          userId,
        },
      ])
      setEmail('')

      if (fetchInstructorData) {
        await fetchInstructorData(userContext)
      }
    } catch (error) {
      const status = error?.response?.status
      if (status === 404 || status === 400) {
        setLookupError(EMAIL_NOT_FOUND_MESSAGE)
      } else {
        setLookupError(EMAIL_NOT_FOUND_MESSAGE)
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [
    addSecondaryTeacher,
    currentSite,
    email,
    fetchInstructorData,
    peopleWithAccess,
    registrationId,
    userContext,
  ])

  return (
    <AddInstructor
      dataTestId={dataTestId}
      email={email}
      handleCancel={handleCancel}
      handleEmailChange={handleEmailChange}
      handleSubmit={handleSubmit}
      inputErrors={inputErrors}
      isSubmitting={isSubmitting}
      lookupError={lookupError}
      peopleWithAccess={peopleWithAccess}
    />
  )
}

AddInstructorContainer.propTypes = {
  addSecondaryTeacher: PropTypes.func,
  currentSite: PropTypes.shape({
    siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  dataTestId: PropTypes.string,
  fetchInstructorData: PropTypes.func,
  handleCancel: PropTypes.func.isRequired,
  instructors: PropTypes.array,
  registrationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  secondaryUserIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  userContext: PropTypes.object,
  userProfile: PropTypes.object,
}

export default AddInstructorContainer
